import { portalLocale, portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Survey results"` — the download dialog (P14-03; AMENDED 2026-10-03).
 *
 * WHAT IT DOES AND DOES NOT CLAIM.
 *
 * The prototype's dialog has one confirm button labelled "Download .csv" which closes the dialog.
 * Here the export is a round trip — `requestSurveyDownload` — and the dialog says so: pressing the
 * button asks for the file, and the link appears only once the gateway has answered with one. A
 * refusal is rendered with the API's own message, never swallowed.
 *
 * AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03): the file is the page. The dialog is handed
 * the page's own filter (window, test conversations, campaign, language, arm) and the API writes one
 * row per conversation of that filter, one column per questionnaire question, recorded values as they
 * are kept and the far end masked (`createVoiceAgentSurveyExport`, audited as
 * `survey_result.exported`). "Open answers in full" is gone with the round model: a survey answer is a
 * recorded value, never free text.
 *
 *   * **CSV only.** `xlsx`, `json` and `pdf` are refused `422 export_format_unavailable` (O-6), so
 *     they are drawn disabled and marked "Not available yet". (Withdrawn 2026-11-03, below.)
 *   * **Conversation links, not recording links.** A portal path — the VoiceAgent's History with the
 *     conversation open, `/agents/<id>?tab=history&conversation=<id>` (`conversationPortalPathOf`;
 *     AMENDED 2026-11-03, `/conversations/<id>` had no page) — so a recording is still opened through
 *     the audited path (design §8.8).
 *   * **People who declined or did not answer** is off by default and changes what the file is a
 *     census of: with it off, every conversation with at least one answer; with it on, every
 *     conversation on the page.
 *
 * AMENDED 2026-11-03 (design §16.7.2 AS AMENDED 2026-11-03): **every format is written.** CSV, Excel (the same
 * table in a workbook: dates and numbers typed, the links clickable, an About sheet), JSON (the same rows keyed by
 * question, with the questions and the filter) and a PDF report (this page's figures and question cards, then the
 * respondents), each with its one-line description; the button reads "Prepare .xlsx" and so on. Both "Include"
 * switches apply to every format: the links are a column of the CSV, hyperlinks in the workbook, a field of each
 * JSON row and a column of the PDF's respondents; the census is the same in all four. A PDF is worded in the
 * person's display language (`portalLocale()`, sent as `display_language`). The link saves the file under the name
 * the API gives it (`wetalk-survey-<agent>-<yyyymmdd-hhmm>.<ext>`).
 *
 * AMENDED 2026-11-03 (owner: "members and viewers should also"): every role that reads the results downloads them,
 * and the dialog says what the role gets. A role the respondents read does not admit (a Viewer) gets the totals — each
 * question's answers and counts — so the two "Include" switches, which only shape one row per conversation, are not
 * drawn and a note says so; a role without `caller_detail.read` (a Member, a Developer, a Campaign manager) is told the
 * callers' numbers are left out. The API decides; the dialog only says it ahead.
 */
import { useMutation } from "@tanstack/react-query";
import { Button, Dialog, Field, Radio } from "@wetalk/ui";
import { useId, useState, type ReactNode } from "react";

import { SwitchControl } from "../billing/switch-control.js";
import { useMemberCan } from "../../shell/member-capability.js";
import {
  DOWNLOAD_FORMAT,
  downloadCensusText,
  downloadExtensionOf,
  downloadReadyText,
} from "./survey-result-copy.js";
import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import type {
  SurveyDownloadFormatView,
  SurveyDownloadView,
  SurveyResultFilterView,
} from "../../api/gateway.js";

import "../billing/billing.css";

export interface SurveyDownloadDialogProps {
  readonly open: boolean;
  readonly voiceAgentId: string;
  /** The page's filter: the file is the page the person was looking at. */
  readonly filter: SurveyResultFilterView;
  /** Shown beside the "include everyone" toggle, so the export's census is stated. */
  readonly conversationCount: number;
  readonly onClose: () => void;
}

export function SurveyDownloadDialog({
  open,
  voiceAgentId,
  filter,
  conversationCount,
  onClose,
}: SurveyDownloadDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const describedBy = useId();
  const member = useMemberCan();
  // What the API will put in the file for this role (survey-answer.ts `surveyRespondentVisible`, `partyVisible`).
  const respondentVisible = member.can("survey_respondent.read");
  const callerVisible = member.can("caller_detail.read");

  const [format, setFormat] = useState<SurveyDownloadFormatView>("csv");
  const [recordingLink, setRecordingLink] = useState(true);
  const [declinedAndNoAnswer, setDeclinedAndNoAnswer] = useState(false);

  const ask = useMutation<SurveyDownloadView, Error>({
    mutationFn: () =>
      gateway.requestSurveyDownload({
        voiceAgentId,
        filter,
        format,
        includeRecordingLink: recordingLink,
        includeDeclinedAndNoAnswer: declinedAndNoAnswer,
        displayLanguage: portalLocale(),
      }),
  });

  const close = (): void => {
    ask.reset();
    onClose();
  };

  const ready = ask.data;

  return (
    <Dialog
      open={open}
      title={portalText("operations", "download_results_68d5c185")}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {ready === undefined ? portalText("operations", "cancel_77dfd213") : portalText("operations", "close_bbfa773e")}
          </Button>
          {ready === undefined ? (
            <Button
              variant="primary"
              disabled={ask.isPending}
              onClick={() => {
                ask.mutate();
              }}
            >
              {ask.isPending
                ? portalText("operations", "preparing_the_file_66cc970a")
                : portalText("operations", "prepare_value_72cb1f7c", { v0: String(downloadExtensionOf(format)) })}
            </Button>
          ) : (
            <a className="btn btn-primary" href={ready.downloadUrl} download={ready.fileName ?? true}>
              {portalText("operations", "download_68fbb1d5")}{ready.fileExtension}
            </a>
          )}
        </>
      }
    >
      <Field label={portalText("operations", "format_041a5dec")}>
        {() => (
          <div className="c-survey-formats">
            {DOWNLOAD_FORMAT.map((one) => (
              <span className="c-survey-format" key={one.value}>
                <Radio
                  name="surveyDownloadFormat"
                  label={one.label}
                  value={one.value}
                  checked={one.value === format}
                  aria-describedby={`${describedBy}-${one.value}`}
                  onChange={() => {
                    setFormat(one.value);
                  }}
                />
                <span className="c-survey-format-dek" id={`${describedBy}-${one.value}`}>
                  {one.dek}
                </span>
              </span>
            ))}
          </div>
        )}
      </Field>

      {respondentVisible ? (
        <Field label={portalText("operations", "include_11c54a59")}>
          {() => (
            <div className="c-survey-includes">
              <SwitchControl
                label={portalText("operations", "links_to_each_conversation_7e3f7540")}
                checked={recordingLink}
                onChange={setRecordingLink}
              />
              <SwitchControl
                label={portalText("operations", "people_who_declined_or_did_not_answer_7be27690")}
                checked={declinedAndNoAnswer}
                onChange={setDeclinedAndNoAnswer}
              />
              <p className="c-note">
                {recordingLink
                  ? portalText("operations", "each_row_links_to_its_conversation_in_wetalk_where_playing_299a21f8")
                  : portalText("operations", "no_links_to_conversations_80095db3")}
              </p>
              <p className="c-note">
                {downloadCensusText(declinedAndNoAnswer, conversationCount)}
              </p>
            </div>
          )}
        </Field>
      ) : (
        <p className="c-note">{portalText("operations", "survey_download.totals_note")}</p>
      )}
      {respondentVisible && !callerVisible ? (
        <p className="c-note">{portalText("operations", "survey_download.no_number_note")}</p>
      ) : null}

      {ready === undefined ? (
        <p className="c-note">
          {portalText("operations", "the_file_is_generated_from_the_answers_as_they_stand_when_7e2d6397")}</p>
      ) : (
        <p className="c-note">{downloadReadyText(ready.rowCount, ready.expiresAtText)}</p>
      )}

      {ask.isError ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "that_export_could_not_be_started_so_there_is_no_file_to_do_f5cedd28")}{" "}
          {apiErrorText(ask.error, "")}
        </p>
      ) : null}
    </Dialog>
  );
}
